// Compact file-attach control shared by every surface that sends words to
// the engine (gate decisions, stop answers, the message composer, /new).
// Files upload to /api/uploads at send time; the caller passes the returned
// batchId with its POST and the server stages/attaches + commits them as
// run inputs.

import { useRef } from "react";
import type { UploadBatchInfo } from "@ideacrunch/shared";

export const UPLOAD_ACCEPT =
  ".pdf,.html,.htm,.txt,.md,.markdown,.csv,.tsv,.json,.docx,.xlsx,.xlsm,.jpg,.jpeg,.png,.webp,.gif,.zip";

export function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${bytes}B`;
}

/** POST the files as one staged batch; returns its batchId. */
export async function uploadBatch(files: File[]): Promise<string> {
  const form = new FormData();
  for (const f of files) form.append("files", f);
  const res = await fetch("/api/uploads", { method: "POST", body: form });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `upload failed (${res.status})`);
  }
  return ((await res.json()) as UploadBatchInfo).batchId;
}

export function AttachFiles({
  files,
  onChange,
  disabled,
  label = "Attach files",
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);

  const add = (incoming: FileList) => {
    const next = [...files];
    for (const f of Array.from(incoming)) {
      if (!next.some((x) => x.name === f.name && x.size === f.size)) {
        next.push(f);
      }
    }
    onChange(next);
  };

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      <button
        className="btn secondary"
        type="button"
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        {label}
      </button>
      <input
        ref={input}
        type="file"
        multiple
        accept={UPLOAD_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files) add(e.target.files);
          e.target.value = "";
        }}
      />
      {files.map((f) => (
        <span key={`${f.name}:${f.size}`} className="chip ghost">
          {f.name} ({fmtSize(f.size)}){" "}
          <button
            type="button"
            aria-label={`remove ${f.name}`}
            disabled={disabled}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--ink-2)",
              padding: "0 2px",
              font: "inherit",
            }}
            onClick={() => onChange(files.filter((x) => x !== f))}
          >
            x
          </button>
        </span>
      ))}
    </div>
  );
}
